import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import { getCurrentUaeDate } from "@utils/date-time";
import { type UseFormReturn } from "react-hook-form";
import {
  FORM_FIELDS,
  TEST_ID_BASE,
  yesNoOptions,
  type PreNominationRequestFormData,
  type PreNominationRequestLabels,
} from "../constants";

interface PreNominationRequestFormSectionProps {
  form: UseFormReturn<PreNominationRequestFormData>;
  labels: PreNominationRequestLabels;
  updateFormData: <K extends keyof PreNominationRequestFormData>(
    key: K,
    value: PreNominationRequestFormData[K],
  ) => void;
  title: string;
}

const PreNominationRequestFormSection = ({
  labels,
  updateFormData,
  title,
}: PreNominationRequestFormSectionProps) => (
  <div className="flex flex-col gap-6">
    <FormSection title={title} className="grid-cols-1 md:grid-cols-2">
      <div className="min-w-0">
        <FormItem<PreNominationRequestFormData, typeof FORM_FIELDS.ETA>
          testId={`${TEST_ID_BASE}-eta-input`}
          name={FORM_FIELDS.ETA}
          type="date-picker"
          label={labels.eta}
          onFieldChange={updateFormData}
          required
          timePicker
          combinedInput
          dateConstraint="not-past"
          minDate={getCurrentUaeDate()}
        />
      </div>

      <div className="hidden md:block" aria-hidden="true" />

      <FormItem<PreNominationRequestFormData, typeof FORM_FIELDS.CALL_SIGN>
        testId={`${TEST_ID_BASE}-call-sign-input`}
        name={FORM_FIELDS.CALL_SIGN}
        type="text"
        label={labels.callSign}
        onFieldChange={updateFormData}
        disabled
        required
      />

      <FormItem<PreNominationRequestFormData, typeof FORM_FIELDS.SUMMER_DWT>
        testId={`${TEST_ID_BASE}-summer-dwt-input`}
        name={FORM_FIELDS.SUMMER_DWT}
        type="number"
        label={labels.summerDwt}
        onFieldChange={updateFormData}
        required
        maxDigits={12}
        decimals={3}
        thousandSeparator
        adornment="MT"
        canBeginWithZero={false}
      />

      <FormItem<PreNominationRequestFormData, typeof FORM_FIELDS.ARRIVAL_DRAFT>
        testId={`${TEST_ID_BASE}-arrival-draft-input`}
        name={FORM_FIELDS.ARRIVAL_DRAFT}
        type="number"
        label={labels.arrivalDraft}
        onFieldChange={updateFormData}
        required
        maxDigits={8}
        decimals={3}
        thousandSeparator
        adornment="m"
        canBeginWithZero={false}
      />

      <FormItem<PreNominationRequestFormData, typeof FORM_FIELDS.DEPARTURE_DRAFT>
        testId={`${TEST_ID_BASE}-departure-draft-input`}
        name={FORM_FIELDS.DEPARTURE_DRAFT}
        type="number"
        label={labels.departureDraft}
        onFieldChange={updateFormData}
        required
        maxDigits={8}
        decimals={3}
        thousandSeparator
        adornment="m"
        canBeginWithZero={false}
      />

      <div className="min-w-0 md:col-span-2">
        <FormItem<PreNominationRequestFormData, typeof FORM_FIELDS.HAS_HULL_PROTRUSION>
          testId={`${TEST_ID_BASE}-has-hull-protrusion-input`}
          name={FORM_FIELDS.HAS_HULL_PROTRUSION}
          type="radio"
          label={labels.hasHullProtrusion}
          onFieldChange={updateFormData}
          required
          options={yesNoOptions}
          horizontalRadioButtons
        />
      </div>
    </FormSection>
  </div>
);

export default PreNominationRequestFormSection;
